/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { gnoment } from 'shared-ui/gnoment';
import get from 'lodash/get';

import * as types from '../constants';
import { getGroupSizeLabel } from './utilsNew';
import {
    isDayBlocked,
    isDayWalkupOnly,
    isDayManagedByLottery,
    isDayBeingBlockedByLottery,
} from './availability-utils-new';

export const getTooltip = ({ dateMap, quotas, day, groupSizeLabel }) => {
    const tooltips = ['Available'];
    if (!dateMap) {
        return tooltips;
    }
    const date = day.startOf('day').format();
    for (const quota in quotas) {
        if (Object.hasOwn(quotas, quota)) {
            let remaining = get(quotas, `[${quota}][${date}].remaining`, 0);
            let total = get(quotas, `[${quota}][${date}].total`, 0);

            if (remaining > 99) {
                remaining = '99+';
            } else if (remaining < 0) {
                remaining = 0;
            }

            if (total > 99) {
                total = '99+';
            } else if (total < 0) {
                total = 0;
            }

            const remainingTotalString = `${remaining} out of ${total}`;
            if (
                quota === types.GROUP_AVAILABILITY_KEY_NAME ||
                quota === types.GROUP_AVAILABILITY_DAILY_KEY_NAME
            ) {
                tooltips.push(`Groups: ${remainingTotalString}`);
            }
            if (
                quota === types.MEMBER_AVAILABILITY_KEY_NAME ||
                quota === types.MEMBER_AVAILABILITY_DAILY_KEY_NAME
            ) {
                const peopleLabel = getGroupSizeLabel(groupSizeLabel);
                tooltips.push(`${peopleLabel}: ${remainingTotalString}`);
            }
            if (quota === types.WATERCRAFT_AVAILABILITY_KEY_NAME) {
                tooltips.push(`Watercraft: ${remainingTotalString}`);
            }
            if (quota === types.MAX_STOCK_KEY_NAME) {
                tooltips.push(`Stock: ${remainingTotalString}`);
            }
        }
    }
    return tooltips;
};

export const getDayType = ({
    permit,
    currentDate,
    dateMap,
    quotas,
    quotaType,
    day,
    divisionId,
    isBypassQuotaCheck,
    aggSeasonRules,
    groupSizeLabel,
}) => {
    const availabilityProps = {
        permit,
        divisionId,
        currentDate,
        quotaType,
        dateMap,
        quotas,
        aggSeasonRules,
        isBypassQuotaCheck,
    };
    const { availabilityCellStatuses } = types;

    if (isDayWalkupOnly(day, availabilityProps)) {
        return {
            className: 'walk-up',
            status: availabilityCellStatuses.WALK_UP,
            tooltips: ['Walkup', 'This is available at the facility on the day of'],
        };
    }

    if (isDayManagedByLottery(day, availabilityProps)) {
        if (isDayBeingBlockedByLottery(day, availabilityProps)) {
            if (isBypassQuotaCheck) {
                return {
                    className: 'lottery',
                    status: availabilityCellStatuses.LOTTERY,
                    tooltips: ['This day is being managed by the lottery'],
                };
            }
            return {
                className: 'lottery',
                status: availabilityCellStatuses.LOTTERY,
                tooltips: [
                    'This day is being managed by the lottery',
                    'Please refer to important dates for application process',
                ],
            };
        }
        return {
            className: 'lottery',
            status: availabilityCellStatuses.LOTTERY,
            tooltips: ['This lottery day is unavailable'],
        };
    }

    if (isDayBlocked(day, availabilityProps)) {
        return {
            className: 'unavailable',
            status: availabilityCellStatuses.UNAVAILABLE,
            tooltips: ['No online reservations available'],
        };
    }

    return {
        className: 'available',
        status: availabilityCellStatuses.AVAILABLE,
        tooltips: getTooltip({ dateMap, quotas, day, groupSizeLabel }),
    };
};

export const generateAvailabilityCellData = (
    divisionData,
    index,
    maskForSelectableDates,
    maskForSelectedDates,
    quotaType,
    isBypassQuotaCheck,
    startDate,
    groupSizeLabel
) => {
    const selectable = maskForSelectableDates;
    const selected = maskForSelectedDates;
    const { availabilityCellStatuses } = types;

    const { displayMap, dateMap, division, permit, quotas, aggSeasonRules } =
        divisionData;

    const permitDate = gnoment(startDate);
    const day = permitDate.clone().add(index, 'day');
    const dateKey = day.utc().startOf('day').format(types.AVAILABILITY_KEY_MAP_FORMAT);

    const isSelectedCell = selected[division.id][dateKey];
    const dayType = getDayType({
        permit,
        currentDate: gnoment().utc(),
        dateMap,
        quotas,
        quotaType,
        day,
        divisionId: division.id,
        isBypassQuotaCheck,
        aggSeasonRules,
        groupSizeLabel,
    });
    const avail = selectable[division.id][dateKey];

    let content = `${(displayMap && displayMap[dateKey]) || 0}`;

    const num = displayMap[dateKey];
    if (num < 0 || !avail) {
        content = '0';
    } else if (num > 99) {
        content = '99+';
    } else if (num > 0 && avail) {
        content = `${num}`;
    }
    if (!avail) {
        // if lottery or walkup, overwrite content here
        if (dayType.className === 'lottery') {
            content = 'L';
        } else if (dayType.className === 'walk-up' && content !== '0') {
            content = 'W';
            dayType.status = availabilityCellStatuses.WALK_UP;
        } else {
            dayType.className = 'unavailable';
            dayType.status = availabilityCellStatuses.UNAVAILABLE;
        }
    } else if (dayType.className === 'unavailable' && avail) {
        dayType.className = 'available';
        dayType.tooltips = getTooltip({ dateMap, quotas, day });
        dayType.status = availabilityCellStatuses.AVAILABLE;
    }

    return {
        key: index,
        content,
        className: dayType.className,
        tooltips: dayType.tooltips,
        isBypassQuotaCheck: !!isBypassQuotaCheck,
        isSelected: isSelectedCell,
        permitId: permit.id,
        divisionId: division.id,
        dateKey,
        status: dayType.status,
    };
};
